import React, { useMemo, useRef, useEffect, useState, useCallback } from 'react';

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function getPercent(hp, maxHp) {
  if (!Number.isFinite(hp) || !Number.isFinite(maxHp) || maxHp <= 0) return 0;
  return clamp((hp / maxHp) * 100, 0, 100);
}

function getHpClass(percent) {
  if (percent <= 25) return 'battleHudBarFill--danger';
  if (percent <= 50) return 'battleHudBarFill--warn';
  return 'battleHudBarFill--ok';
}

/** Affichage lisible du login Twitch (overlay uniquement). */
function formatMemberLogin(login) {
  if (!login || typeof login !== 'string') return '';
  const s = login.trim();
  if (!s) return '';
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

function formatCountdown(totalSec) {
  if (totalSec == null || totalSec < 0) return '0:00';
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/** Compte à rebours jusqu’à registrationDeadlineAt (ISO serveur). */
function useRegistrationRemainingSec(deadlineIso) {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!deadlineIso) return undefined;
    const id = setInterval(() => setTick((x) => x + 1), 250);
    return () => clearInterval(id);
  }, [deadlineIso]);

  return useMemo(() => {
    if (!deadlineIso) return null;
    const end = Date.parse(deadlineIso);
    if (!Number.isFinite(end)) return null;
    return Math.max(0, Math.ceil((end - Date.now()) / 1000));
  }, [deadlineIso, tick]);
}

/**
 * Particule d'effet temporaire (explosion ou cœur) sur une barre HP.
 * Disparaît automatiquement après l'animation.
 */
function BattleFxParticle({ type, onDone }) {
  useEffect(() => {
    const t = setTimeout(onDone, 750);
    return () => clearTimeout(t);
  }, [onDone]);

  return (
    <span
      className={type === 'damage' ? 'battleFxExplosion' : 'battleFxHeart'}
      aria-hidden
    >
      {type === 'damage' ? '💥' : '❤️'}
    </span>
  );
}

/**
 * Détecte les changements de HP et retourne les effets visuels actifs
 * ainsi que la classe flash à appliquer sur la barre.
 */
function useHpFx(hp) {
  const prevRef = useRef(hp);
  const [particles, setParticles] = useState([]);
  const [barFlash, setBarFlash] = useState(null);

  useEffect(() => {
    const prev = prevRef.current;
    if (prev === null || hp === prev) {
      prevRef.current = hp;
      return;
    }

    const isDamage = hp < prev;
    const isHeal = hp > prev;
    prevRef.current = hp;

    if (!isDamage && !isHeal) return;

    const id = Date.now() + Math.random();
    setParticles((p) => [...p, { id, type: isDamage ? 'damage' : 'heal' }]);
    setBarFlash(isDamage ? 'damaged' : 'healed');

    const flashTimer = setTimeout(() => setBarFlash(null), 520);
    return () => clearTimeout(flashTimer);
  }, [hp]);

  const removeParticle = useCallback((id) => {
    setParticles((p) => p.filter((x) => x.id !== id));
  }, []);

  return { particles, barFlash, removeParticle };
}

/**
 * Barre HP d'une équipe — style Tekken.
 * @param {'left'|'right'} side Côté de l'écran (gauche = team1, droite = team2).
 */
function BattleHpSide({ label, hp, maxHp, side, members }) {
  const percent = useMemo(() => getPercent(hp, maxHp), [hp, maxHp]);
  const { particles, barFlash, removeParticle } = useHpFx(hp);
  const roster = Array.isArray(members) ? members : [];

  return (
    <div className={`battleHudSide battleHudSide--${side}`}>
      <div className="battleHudSideHeader">
        <span className="battleHudTeamName">{label}</span>
        <span className="battleHudHpValue">{hp} PV</span>
      </div>
      {roster.length > 0 && (
        <ul className={`battleHudMembers battleHudMembers--${side}`} aria-hidden>
          {roster.map((login) => (
            <li key={login} className="battleHudMemberChip">
              {formatMemberLogin(login)}
            </li>
          ))}
        </ul>
      )}
      <div className="battleHudBarWrapper">
        <div
          className={`battleHudBar battleHudBar--${side}${barFlash ? ` battleHudBar--${barFlash}` : ''}`}
          role="presentation"
        >
          <div
            className={`battleHudBarFill ${getHpClass(percent)}`}
            style={{ width: `${percent}%` }}
          />
        </div>
        {particles.map(({ id, type }) => (
          <BattleFxParticle
            key={id}
            type={type}
            onDone={() => removeParticle(id)}
          />
        ))}
      </div>
    </div>
  );
}

export default function BattleOverlay({ state }) {
  const isActive = Boolean(state?.active);
  const deadlineIso =
    state && typeof state.registrationDeadlineAt === 'string'
      ? state.registrationDeadlineAt
      : null;

  const remainingInscriptionSec = useRegistrationRemainingSec(
    isActive ? deadlineIso : null
  );

  const prevDeadlineRef = useRef(null);
  const [fightSplash, setFightSplash] = useState(false);
  useEffect(() => {
    const prev = prevDeadlineRef.current;
    prevDeadlineRef.current = deadlineIso;
    if (prev != null && deadlineIso == null && isActive) {
      setFightSplash(true);
      const t = setTimeout(() => setFightSplash(false), 2600);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [deadlineIso, isActive]);

  if (!state) return null;

  const maxHp = Number.isFinite(Number(state?.maxHp)) && Number(state.maxHp) > 0
    ? Number(state.maxHp)
    : 100;
  const team1Hp = Number(state?.team1Hp ?? 0);
  const team2Hp = Number(state?.team2Hp ?? 0);
  const team1Members = Array.isArray(state?.team1Members) ? state.team1Members : [];
  const team2Members = Array.isArray(state?.team2Members) ? state.team2Members : [];

  const inscriptionActive =
    isActive &&
    deadlineIso != null &&
    remainingInscriptionSec != null &&
    remainingInscriptionSec > 0;

  const statusMain = (() => {
    if (!isActive) return 'TERMINÉE';
    if (inscriptionActive) return 'INSCRIPTION';
    return 'COMBAT';
  })();

  const showResult = state.active === false && state.winnerTeam;
  const winnerLabel =
    state.winnerTeam === 'team1'
      ? 'Victoire Équipe 1 🏆'
      : state.winnerTeam === 'team2'
        ? 'Victoire Équipe 2 🏆'
        : null;

  return (
    <div className="battleHud" aria-hidden>
      <div className="battleHudInner">
        <div className="battleHudSides">
          <BattleHpSide
            label="Équipe 1"
            hp={team1Hp}
            maxHp={maxHp}
            side="left"
            members={team1Members}
          />

          <div className="battleHudCenter">
            <div className="battleHudVsBadge">
              {fightSplash && (
                <div className="battleHudFightSplash" aria-hidden>
                  COMBAT !
                </div>
              )}
              <span
                className={`battleHudStatusDot ${
                  state.active ? 'battleHudStatusDot--live' : 'battleHudStatusDot--ended'
                }`}
              />
              <span className="battleHudVsText">VS</span>
              <span
                className={`battleHudStatus ${
                  state.active ? 'battleHudStatus--live' : 'battleHudStatus--ended'
                }`}
              >
                {statusMain}
              </span>
              {inscriptionActive && remainingInscriptionSec != null && (
                <span className="battleHudCountdown" aria-hidden>
                  {formatCountdown(remainingInscriptionSec)}
                </span>
              )}
              <span className="battleHudTitle">Battle viewers</span>
            </div>
          </div>

          <BattleHpSide
            label="Équipe 2"
            hp={team2Hp}
            maxHp={maxHp}
            side="right"
            members={team2Members}
          />
        </div>

        {showResult && winnerLabel && (
          <div className="battleHudResult">{winnerLabel}</div>
        )}
      </div>
    </div>
  );
}
